import StarrySkyBackground from "@/components/StarrySkyBackground";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/services/auth-service";
import { motion } from "framer-motion";
import {
    Book,
    GraduationCap,
    MapPin,
    Palette,
    Sparkles,
    Star,
    Users,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

const ExampleStoriesPage = () => {
    const [stories, setStories] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchStories = async () => {
            try {
                const response = await api.get("/api/example-stories/");
                const data = response.data;
                setStories(data);
            } catch (error) {
                console.error("Error fetching stories:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchStories();
    }, []);

    const StorySkeleton = () => (
        <Card className="transition-shadow hover:shadow-lg">
            <CardHeader>
                <Skeleton className="w-3/4 h-6" />
                <Skeleton className="w-1/2 h-4 mt-2" />
            </CardHeader>
            <CardContent>
                <Skeleton className="w-full h-20" />
            </CardContent>
        </Card>
    );

    return (
        <div className="container px-4 py-8 mx-auto">
            <StarrySkyBackground
                showAurora={true}
                showClouds={true}
                showShootingStar={false}
            />
            <div className="mb-12 text-center">
                <h1 className="mt-4 mb-8 text-4xl font-bold text-black dark:text-sky-50">
                    Discover the Magic of Personalized Stories
                </h1>
                <p className="text-lg text-foreground text-center relative z-[1] md:z-0 lg:z-0 mb-8 bg-primary/90 py-3 rounded-lg text-slate-50">
                    Create unique, AI-powered stories tailored to your child's age,
                    interests, and learning journey.
                </p>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {loading
                    ? Array(6)
                        .fill(0)
                        .map((_, index) => <StorySkeleton key={index} />)
                    : stories.map((story) => (
                        <Card
                            key={story.id}
                            className="transition-all duration-300 cursor-pointer h-fit group hover:shadow-lg hover:-translate-y-1"
                            onClick={() => navigate(`/story/${story.id}/`)}
                        >
                            <CardHeader>
                                <CardTitle className="flex items-center mb-3 text-xl font-semibold transition-colors group-hover:text-primary">
                                    <Book className="w-5 h-5 mr-2" />
                                    {story.title}
                                </CardTitle>
                                <div className="flex flex-wrap gap-2">
                                    <Badge className="text-white capitalize dark:text-white bg-primary/50 dark:bg-primary/50">
                                        <GraduationCap className="inline w-4 h-4 mr-1" />
                                        {story.theme}
                                    </Badge>
                                    <Badge className="text-white capitalize dark:text-white bg-lime-500 dark:bg-lime-500">
                                        <MapPin className="inline w-4 h-4 mr-1" />
                                        {story.setting}
                                    </Badge>
                                    <Badge className="text-white capitalize bg-orange-500 dark:text-white dark:bg-orange-500">
                                        <Palette className="inline w-4 h-4 mr-1" />
                                        {story.artStyle}
                                    </Badge>
                                </div>
                            </CardHeader>
                            <CardContent className="p-0">
                                {story.coverImageUrl && (
                                    <img
                                        src={story.coverImageUrl}
                                        alt={story.title}
                                        className="object-cover w-full h-[300px] rounded-b-xl"
                                    />
                                )}
                            </CardContent>
                        </Card>
                    ))}
            </div>

            <div className="mt-24 text-center">
                <div className="grid grid-cols-1 gap-6 mb-12 md:grid-cols-3">
                    <Card className="bg-card/70">
                        <CardContent className="pt-6 text-center">
                            <Sparkles className="w-12 h-12 mx-auto mb-4 text-primary" />
                            <h3 className="mb-2 font-semibold">Personalized Magic</h3>
                            <p className="text-sm text-foreground">
                                Every story adapts to your child's interests and needs
                            </p>
                        </CardContent>
                    </Card>

                    <Card className="bg-card/70">
                        <CardContent className="pt-6 text-center">
                            <Users className="w-12 h-12 mx-auto mb-4 text-primary" />
                            <h3 className="mb-2 font-semibold">Age-Appropriate</h3>
                            <p className="text-sm text-foreground">
                                Content perfectly matched to your child's reading level
                            </p>
                        </CardContent>
                    </Card>

                    <Card className="bg-card/70">
                        <CardContent className="pt-6 text-center">
                            <Star className="w-12 h-12 mx-auto mb-4 text-primary" />
                            <h3 className="mb-2 font-semibold">Engaging Art</h3>
                            <p className="text-sm text-foreground">
                                Beautiful illustrations in various artistic styles
                            </p>
                        </CardContent>
                    </Card>
                </div>
            </div>

            <div className="p-8 text-center border rounded-lg bg-card/70 w-fit place-self-center">
                <Link to="/signup">
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="px-8 py-3 font-medium text-black rounded-lg shadow-lg bg-primary/90 hover:bg-primary/90"
                    >
                        Start Creating Your Stories
                    </motion.button>
                </Link>
                <p className="mt-4 text-sm text-foreground">
                    Join thousands of families creating magical moments with personalized
                    stories
                </p>
            </div>
        </div>
    );
};

export default ExampleStoriesPage;
